import embla_carousel_autoplay from "embla-carousel-autoplay";
import embla_carousel_fade from "embla-carousel-fade";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
const DEFAULT_PLUGINS = [
    {
        type: 'wheelGestures'
    }
];
const MIN_DELAY = 500;
const VALID_TYPES = [
    'wheelGestures',
    'fade',
    'autoplay'
];
const VALID_MODES = [
    'hover',
    'inview',
    'init'
];
const isDev = 'production' !== process.env.NODE_ENV;
function warn(message) {
    if (isDev) console.warn(message);
}
function isValidType(type) {
    return 'string' == typeof type && VALID_TYPES.includes(type);
}
function isValidMode(mode) {
    return 'string' == typeof mode && VALID_MODES.includes(mode);
}
function validateAutoplayConfig(config) {
    const validated = {
        ...config
    };
    if (validated.mode && !isValidMode(validated.mode)) {
        warn(`[Carousel] Invalid autoplay mode: "${validated.mode}". Defaulting to idle.`);
        validated.mode = void 0;
    }
    if (void 0 !== validated.delay && validated.delay < MIN_DELAY) {
        warn(`[Carousel] autoplay delay ${validated.delay}ms below ${MIN_DELAY}ms minimum. Clamping.`);
        validated.delay = MIN_DELAY;
    }
    if (validated.syncGlobally && void 0 !== validated.override) warn("[Carousel] syncGlobally and override should not be used together. These features have conflicting control models: syncGlobally pauses/resumes all carousels together, while override gives external logic individual control. Use syncGlobally with mode-based triggers (hover/inview/init), or use override without syncGlobally.");
    return validated;
}
function warnFadeAutoplayConflict(plugins) {
    const hasFade = plugins.some((c)=>'fade' === c.type);
    const autoplay = plugins.find((c)=>'autoplay' === c.type);
    if (hasFade && autoplay?.delay && autoplay.delay < 1000) warn(`[Carousel] autoplay delay ${autoplay.delay}ms with fade may cause visual overlap. Recommended: 1000ms+.`);
}
function normalizePlugins(raw) {
    if (!Array.isArray(raw)) {
        if (void 0 !== raw) warn('[Carousel] plugins must be an array. Using default plugins.');
        return DEFAULT_PLUGINS;
    }
    const filtered = raw.filter(Boolean);
    if (0 === filtered.length) {
        warn('[Carousel] Empty plugins array. Adding wheelGestures for accessibility.');
        return DEFAULT_PLUGINS;
    }
    const seen = new Set();
    const deduped = [];
    const duplicates = [];
    for (const config of filtered){
        if (!isValidType(config.type)) {
            warn(`[Carousel] Unknown plugin type: "${String(config.type)}". Ignoring.`);
            continue;
        }
        if (seen.has(config.type)) {
            duplicates.push(config.type);
            continue;
        }
        seen.add(config.type);
        deduped.push('autoplay' === config.type ? validateAutoplayConfig(config) : config);
    }
    if (duplicates.length > 0) warn(`[Carousel] Duplicate plugins: ${[
        ...new Set(duplicates)
    ].join(', ')}. Using first occurrence.`);
    if (0 === deduped.length) {
        warn('[Carousel] No valid plugins after validation. Adding wheelGestures for accessibility.');
        return DEFAULT_PLUGINS;
    }
    warnFadeAutoplayConflict(deduped);
    return deduped;
}
function createEmblaPlugins(configs) {
    const plugins = [];
    for (const config of configs)switch(config.type){
        case 'wheelGestures':
            plugins.push(WheelGesturesPlugin());
            break;
        case 'fade':
            plugins.push(embla_carousel_fade());
            break;
        case 'autoplay':
            plugins.push(embla_carousel_autoplay({
                playOnInit: false,
                delay: config.delay ?? 5000,
                stopOnMouseEnter: false,
                stopOnFocusIn: false,
                stopOnInteraction: false
            }));
            break;
        default:
            {
                const exhaustiveCheck = config;
                throw new Error(`Unsupported carousel plugin type: ${exhaustiveCheck}`);
            }
    }
    return plugins;
}
export { createEmblaPlugins, normalizePlugins };
